"use client"

import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  UserIcon,
  Loader2,
  Eye,
  EyeOff,
  X,
  CheckCircle,
  Clock,
  Camera,
  Mail,
  Phone,
  User,
  Users,
  Lock,
  Save,
  ArrowLeft,
} from "lucide-react"
import { generatePresignedUrl, postStudentProfilePhoto } from "../postFileAws"
import { postAws } from "../aws"
import { getStudentInfo } from "./api"
import { useNavigate } from "react-router-dom"

const EditStudentProfile = () => {
  const navigate = useNavigate()
  const [student, setStudent] = useState({
    studentId: "",
    studentName: "",
    studentEmail: "",
    phoneNumber: "",
    parentName: "",
    parentNumber: "",
    parentEmail: "",
    profilePhotoS3Uri: "",
  })
  const [isEditing, setIsEditing] = useState(false)
  const [profileImage, setProfileImage] = useState("")
  const [imagePreview, setImagePreview] = useState("")
  const fileInputRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const [otp, setOtp] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")
  const [otpSent, setOtpSent] = useState(false)
  const [otpError, setOtpError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)
  const [timeLeft, setTimeLeft] = useState(300) // 5 minutes
  const [isTimedOut, setIsTimedOut] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isOtpLoading, setIsOtpLoading] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        setIsLoading(true)
        const studentId = localStorage.getItem("studentId")
        if (!studentId) return

        const response = await getStudentInfo(studentId)
        if (response?.statusCode === 200 || response?.statusCode === 201) {
          const data = response.body.data
          setStudent({ ...data, studentId })

          if (data.profilePhotoS3Uri) {
            const presignedUrl = await generatePresignedUrl(data.profilePhotoS3Uri)
            setProfileImage(presignedUrl)
            setImagePreview(presignedUrl)
          }
        }
      } catch (error) {
        console.error("Error fetching student data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStudentData()
  }, [])

  useEffect(() => {
    let timer
    if (showChangePasswordModal && otpSent && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timer)
            setIsTimedOut(true)
            return 0
          }
          return prevTime - 1
        })
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [showChangePasswordModal, otpSent, timeLeft])

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setStudent((prevStudent) => ({
      ...prevStudent,
      [name]: value,
    }))
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing)
  }

  const handleImageClick = () => {
    fileInputRef.current.click()
  }

  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (file) {
      setImagePreview(URL.createObjectURL(file))
      setProfileImage(file)
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const updatedStudent = { ...student }

      if (profileImage instanceof File) {
        const uploadResult = await postStudentProfilePhoto(profileImage, student.studentId, profileImage.name)
        if (uploadResult && uploadResult.uri) {
          updatedStudent.profilePhotoS3Uri = uploadResult.uri
        }
      }

      const apiData = {
        request: "update-student-profile",
        body: {
          studentId: updatedStudent.studentId,
          studentName: updatedStudent.studentName,
          studentEmail: updatedStudent.studentEmail,
          phoneNumber: updatedStudent.phoneNumber,
          parentName: updatedStudent.parentName,
          parentNumber: updatedStudent.parentNumber,
          parentEmail: updatedStudent.parentEmail,
          profilePhotoS3Uri: updatedStudent.profilePhotoS3Uri,
        },
      }

      Object.keys(apiData.body).forEach((key) => apiData.body[key] === undefined && delete apiData.body[key])

      const response = await postAws("https://api-dev.aurait.ai/auth/student-auth", "POST", apiData)

      if (response && response.statusCode === 200) {
        console.log("Profile updated successfully")
        setStudent(updatedStudent)
        setIsEditing(false)
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 3000)
      } else {
        throw new Error("Failed to update profile")
      }
    } catch (error) {
      console.error("Error saving changes:", error)
      alert("Failed to save changes. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  const handleChangePassword = async () => {
    setShowChangePasswordModal(true)
  }

  const handleSendOtp = async () => {
    try {
      setIsOtpLoading(true)
      setOtpError("")
      const otpData = {
        body: { studentEmail: student.studentEmail },
        request: "forgot-password-Student",
      }
      const response = await postAws(`https://api-dev.aurait.ai/forgotpassword/student`, "POST", otpData)

      if (response.statusCode === 200) {
        setOtpSent(true)
        setTimeLeft(300)
        setIsTimedOut(false)
      } else {
        throw new Error("Failed to send OTP")
      }
    } catch (error) {
      console.error("Error sending OTP:", error)
      setOtpError("Failed to send OTP. Please try again.")
    } finally {
      setIsOtpLoading(false)
    }
  }

  const handleResendOtp = async () => {
    setIsResending(true)
    await handleSendOtp()
    setIsResending(false)
    setTimeLeft(300)
    setIsTimedOut(false)
    setOtpError("")
  }

  const handleVerifyOtp = async (e) => {
    e.preventDefault()
    setIsVerifying(true)
    setOtpError("")
    setPasswordError("")

    if (newPassword !== confirmNewPassword) {
      setPasswordError("Passwords do not match")
      setIsVerifying(false)
      return
    }

    try {
      const resetPasswordData = {
        body: {
          teacherEmail: student.studentEmail,
          otp: otp,
          password: newPassword,
        },
        request: "verify-otp-Student",
      }
      const response = await postAws(`https://api-dev.aurait.ai/forgotpassword/student`, "POST", resetPasswordData)

      if (response.statusCode === 401) {
        setOtpError(response.body.error || "Invalid OTP, please try again")
      } else if (response.statusCode === 201) {
        setShowChangePasswordModal(false)
        setShowSuccessModal(true)
        setTimeout(() => {
          setShowSuccessModal(false)
        }, 3000)
      } else {
        throw new Error("Unexpected response from server")
      }
    } catch (error) {
      setOtpError("An error occurred. Please try again.")
    } finally {
      setIsVerifying(false)
    }
  }

  // Function to format email for display
  const formatEmail = (email) => {
    if (!email) return "No email provided"

    if (email.length > 25) {
      const atIndex = email.indexOf("@")
      if (atIndex > 0) {
        const username = email.substring(0, atIndex)
        const domain = email.substring(atIndex)

        if (username.length > 15) {
          return username.substring(0, 12) + "..." + domain
        }
      }
    }
    return email
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="bg-white p-8 rounded-xl shadow-xl">
            <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-6" />
            <p className="text-xl font-semibold text-gray-700">Loading your profile...</p>
            <p className="text-gray-500 mt-2">Please wait while we fetch your information</p>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      {/* Back button - Only visible on desktop */}
      <div className="max-w-7xl mx-auto mb-6 hidden md:block">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center text-gray-700 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          <span>Back to Dashboard</span>
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Success notification */}
          <AnimatePresence>
            {saveSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                className="bg-green-100 border-l-4 border-green-500 p-4 flex items-center"
              >
                <CheckCircle className="text-green-500 w-6 h-6 mr-3" />
                <p className="text-green-700">Profile updated successfully!</p>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="md:flex">
            {/* Left sidebar */}
            <div className="md:w-1/3 bg-gradient-to-br from-blue-600 to-indigo-700 p-8 text-white">
              {/* Back button for mobile - inside blue gradient */}
              <div className="md:hidden mb-6">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="flex items-center text-white hover:text-blue-100 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  <span>Back to Dashboard</span>
                </button>
              </div>

              <div className="text-center">
                <div className="relative inline-block">
                  <motion.div
                    whileHover={isEditing ? { scale: 1.05 } : {}}
                    className="relative cursor-pointer group"
                    onClick={isEditing ? handleImageClick : undefined}
                  >
                    {profileImage ? (
                      <div className="relative w-40 h-40 mx-auto">
                        <img
                          src={imagePreview || profileImage}
                          alt="Student"
                          className="w-40 h-40 rounded-full border-4 border-white shadow-lg mx-auto object-cover"
                        />
                        {isEditing && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <Camera className="w-8 h-8 text-white" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-40 h-40 rounded-full border-4 border-white shadow-lg mx-auto flex items-center justify-center bg-indigo-800">
                        <UserIcon className="w-20 h-20 text-white opacity-70" />
                        {isEditing && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <Camera className="w-8 h-8 text-white" />
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </div>
                <h2 className="text-3xl font-bold mt-6 mb-2">{student.studentName || "No name provided"}</h2>
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500 bg-opacity-30 text-sm">
                  <User className="w-4 h-4 mr-1" />
                  <span>Student</span>
                </div>
              </div>

              <div className="mt-10 space-y-6">
                <div className="flex items-center">
                  <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div className="overflow-hidden max-w-[calc(100%-60px)]">
                    <p className="text-sm opacity-75">Email</p>
                    <p className="font-medium break-all">{formatEmail(student.studentEmail)}</p>
                    {student.studentEmail && student.studentEmail.length > 25 && (
                      <div
                        className="text-xs opacity-75 mt-1 hover:opacity-100 transition-opacity cursor-pointer"
                        title={student.studentEmail}
                      >
                       
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm opacity-75">Phone</p>
                    <p className="font-medium">{student.phoneNumber || "No phone number provided"}</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="bg-white bg-opacity-20 p-3 rounded-full mr-4">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="overflow-hidden max-w-[calc(100%-60px)]">
                    <p className="text-sm opacity-75">Parent</p>
                    <p className="font-medium break-all">{student.parentName || "No parent name provided"}</p>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full py-3 px-4 bg-white text-indigo-700 rounded-xl font-medium shadow-lg hover:shadow-xl transition duration-300 flex items-center justify-center"
                  onClick={handleChangePassword}
                >
                  <Lock className="w-5 h-5 mr-2" />
                  Change Password
                </motion.button>
              </div>
            </div>

            {/* Main content */}
            <div className="md:w-2/3 p-8">
              <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800">Profile Information</h1>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-5 py-2.5 rounded-xl font-medium shadow-md transition duration-300 flex items-center ${
                    isEditing
                      ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700"
                  }`}
                  onClick={handleEditToggle}
                >
                  {isEditing ? "Cancel" : <span className="md:inline hidden">Edit Profile</span>}
                  {isEditing ? "" : <span className="md:hidden inline">Edit</span>}
                </motion.button>
              </div>

              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-blue-500" />
                  Personal Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="studentName"
                        value={student.studentName}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                        placeholder="Enter your full name"
                      />
                    ) : (
                      <div className="text-lg text-gray-800 p-3 bg-white border border-gray-100 rounded-xl">
                        {student.studentName || "No name provided"}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    {isEditing ? (
                      <input
                        type="email"
                        name="studentEmail"
                        value={student.studentEmail}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                        placeholder="Enter your email"
                      />
                    ) : (
                      <div className="text-lg text-gray-800 p-3 bg-white border border-gray-100 rounded-xl">
                        <div className="break-all">{student.studentEmail || "No email provided"}</div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={student.phoneNumber}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                        placeholder="Enter your phone number"
                      />
                    ) : (
                      <div className="text-lg text-gray-800 p-3 bg-white border border-gray-100 rounded-xl">
                        {student.phoneNumber || "No phone number provided"}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-blue-500" />
                  Parent Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="parentName"
                        value={student.parentName}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                        placeholder="Enter parent's name"
                      />
                    ) : (
                      <div className="text-lg text-gray-800 p-3 bg-white border border-gray-100 rounded-xl">
                        {student.parentName || "No parent name provided"}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent Email</label>
                    {isEditing ? (
                      <input
                        type="email"
                        name="parentEmail"
                        value={student.parentEmail}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                        placeholder="Enter parent's email"
                      />
                    ) : (
                      <div className="text-lg text-gray-800 p-3 bg-white border border-gray-100 rounded-xl">
                        <div className="break-all">{student.parentEmail || "No parent email provided"}</div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parent Phone</label>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="parentNumber"
                        value={student.parentNumber}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                        placeholder="Enter parent's phone number"
                      />
                    ) : (
                      <div className="text-lg text-gray-800 p-3 bg-white border border-gray-100 rounded-xl">
                        {student.parentNumber || "No parent phone provided"}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isEditing && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-8 flex justify-end"
                >
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition duration-300 flex items-center"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-5 h-5 text-white animate-spin mr-2" />
                        Saving Changes...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        Save Changes
                      </>
                    )}
                  </motion.button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Change Password Modal */}
      <AnimatePresence>
        {showChangePasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white p-8 rounded-2xl shadow-2xl relative max-w-md w-full"
            >
              <button
                onClick={() => setShowChangePasswordModal(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 bg-gray-100 p-2 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="text-center mb-6">
                <div className="bg-blue-100 p-3 rounded-full inline-flex mb-4">
                  <Lock className="h-8 w-8 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Change Password</h2>
                <p className="text-gray-500 mt-1">Secure your account with a new password</p>
              </div>

              {!otpSent ? (
                <button
                  onClick={handleSendOtp}
                  disabled={isOtpLoading}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-xl font-medium shadow-md hover:shadow-lg transition duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isOtpLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      Sending OTP...
                    </>
                  ) : (
                    "Send OTP to Your Email"
                  )}
                </button>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                      Enter OTP Code
                    </label>
                    <input
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-widest"
                      required
                      disabled={isTimedOut}
                      maxLength={6}
                      placeholder="• • • • • •"
                    />
                    <div className="mt-2 flex items-center justify-end">
                      <Clock className="h-4 w-4 text-gray-500 mr-1" />
                      <span className={`text-sm ${isTimedOut ? "text-red-500 font-medium" : "text-gray-500"}`}>
                        {isTimedOut ? "OTP Expired" : formatTime(timeLeft)}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmNewPassword ? "text" : "password"}
                        id="confirmNewPassword"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                      >
                        {showConfirmNewPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {otpError && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                      <p className="text-red-700 text-sm">{otpError}</p>
                    </div>
                  )}

                  {passwordError && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                      <p className="text-red-700 text-sm">{passwordError}</p>
                    </div>
                  )}

                  {isTimedOut ? (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="w-full bg-indigo-600 text-white py-3 px-4 rounded-xl font-medium shadow-md hover:bg-indigo-700 transition duration-300 flex items-center justify-center"
                      disabled={isResending}
                    >
                      {isResending ? (
                        <>
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Resending OTP...
                        </>
                      ) : (
                        "Resend OTP"
                      )}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-xl font-medium shadow-md hover:shadow-lg transition duration-300 flex items-center justify-center"
                      disabled={isVerifying}
                    >
                      {isVerifying ? (
                        <>
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Verifying...
                        </>
                      ) : (
                        "Reset Password"
                      )}
                    </button>
                  )}
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-md"
            >
              <div className="bg-green-100 p-4 rounded-full inline-flex mb-6">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Password Changed Successfully</h2>
              <p className="text-gray-600 mb-6">
                Your password has been updated. You can now use your new password to log in.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-medium shadow-md hover:shadow-lg transition duration-300"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default EditStudentProfile

